import React, { useState, useRef, useEffect, useMemo } from 'react';
import TableGrid from '../../../components/UI/TableGrid';
import Pagination from '../../../components/UI/TableGrid/Pagination';
import { Modal } from 'react-bootstrap';
import FormInputs from '../../../components/UI/FormInputs';
import Buttons from '../../../components/UI/Buttons';
import IncidentService from '../../../services/IncidentService';
import EmrVisitsService from '../../../services/EmrVisitsService';
import ReferralProviderService from '../../../services/ReferralProviderService';
import DropDownService from '../../../services/DropDownService';
import { toQueryString } from '../../../utils/commonUtils';
import { OK } from '../../../utils/constant';
import PERMISSIONS from '../../../utils/AclPermissions';

const MissingClaimNumbersFullScreen = ({ 
  isOpen, 
  onClose, 
  initialPosition,
  filters,
  Loader,
  Toast,
  onIncidentDeleted
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [positionState, setPositionState] = useState(null);
  const originalPositionRef = useRef(null);
  const hasExpandedRef = useRef(false);
  const [allData, setAllData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRecords: 0,
    sort_by: 'date_of_incident',
    sort_order: 'desc'
  });
  const [showClaimNumberModal, setShowClaimNumberModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [claimNumber, setClaimNumber] = useState('');
  const [isSavingClaimNumber, setIsSavingClaimNumber] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);

  const findCompanyName = (id) => {
    const obj = companyList.find((val) => val.value === id);
    if (obj) {
      return obj.label;
    }
    return '-';
  };

  useEffect(() => {
    if (isOpen && initialPosition) {
      hasExpandedRef.current = false;
      originalPositionRef.current = initialPosition;
      setPositionState(initialPosition);
      const t = setTimeout(() => {
        setPositionState(null);
        hasExpandedRef.current = true;
      }, 50);
      return () => clearTimeout(t);
    }
    if (!isOpen) {
      setPositionState(null);
      originalPositionRef.current = null;
      hasExpandedRef.current = false;
    }
  }, [isOpen, initialPosition]);

  useEffect(() => {
    if (isOpen) {
      setIsFullscreen(true);
      fetchCompanyList();
      fetchData();
    } else {
      setIsFullscreen(false);
      setPositionState(null);
      setAllData([]);
      setTableData([]);
    }
  }, [isOpen]);

  // Sort allData by sort_by and sort_order (TableGrid pattern)
  const sortedData = useMemo(() => {
    if (allData.length === 0 || !pagination.sort_by) return allData;
    const sortKey = pagination.sort_by;
    const isDesc = (pagination.sort_order || 'asc').toLowerCase() === 'desc';
    const mult = isDesc ? -1 : 1;
    return [...allData].sort((a, b) => {
      const aVal = a?.data?.[sortKey] ?? '';
      const bVal = b?.data?.[sortKey] ?? '';
      if (sortKey === 'date_of_incident') {
        const aDate = aVal ? new Date(aVal) : new Date(0);
        const bDate = bVal ? new Date(bVal) : new Date(0);
        return mult * (aDate - bDate);
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      const cmp = aStr < bStr ? -1 : (aStr > bStr ? 1 : 0);
      return mult * cmp;
    });
  }, [allData, pagination.sort_by, pagination.sort_order]);

  // Apply pagination when in fullscreen mode
  useEffect(() => {
    if (isFullscreen && sortedData.length > 0) {
      const start = (pagination.page - 1) * pagination.perPage;
      const end = start + pagination.perPage;
      const paginatedData = sortedData.slice(start, end);
      setTableData(paginatedData);
    }
  }, [pagination.page, pagination.perPage, sortedData, isFullscreen]);

  const fetchCompanyList = async () => {
    try {
      const response = await DropDownService.getCompanyList(null);
      if (response && Array.isArray(response)) {
        setCompanyList(response);
      }
    } catch (error) {
      console.error('Error fetching company list:', error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const query = {
        page: 1,
        perPage: 1000, // Get all records for client-side pagination
        sort_by: 'created_at',
        sort_order: 'desc',
        search: {
          ...filters,
          dashboard_filter: 'pending_claim'
        }
      };
      const queryString = toQueryString(query);
      
      if (Loader && typeof Loader === 'function') {
        Loader(true);
      }
      
      const response = await IncidentService.getIncidentList(queryString);
      
      if (response && response.data && response.data.data) {
        const incidents = response.data.data.map((incident, index) => {
          const actionItems = [
            {
              className: "btn btn-icon btn-transparent-dark",
              iconType: "Remove",
              title: "Delete",
              acl: PERMISSIONS.INCIDENT.DELETE,
              isDelete: true,
              clickHandler: () => deleteIncidentHandler(incident),
            },
          ];

          actionItems.push({
            className: "btn btn-icon btn-transparent-dark",
            iconType: "MeatBall",
            title: "More",
            iconDropDownList: [
              {
                className: "btn btn-icon btn-transparent-dark",
                iconType: "Print",
                label: "View Referral",
                title: "View Referral",
                clickHandler: () => viewReferralHandler(incident)
              },
              {
                className: "btn btn-icon btn-transparent-dark",
                iconType: "Print",
                label: "View Auth",
                title: "Compliance",
                clickHandler: () => {}
              },
              {
                className: "btn btn-icon btn-transparent-dark",
                iconType: "Print",
                label: "Export List",
                title: "Compliance",
                clickHandler: () => {}
              },
              {
                className: "btn btn-icon btn-transparent-dark",
                iconType: "Print",
                label: "Add Claim Number",
                title: "Edit Claim Number",
                clickHandler: () => openClaimNumberModal(incident)
              },
            ],
          });

          return {
            companyIndex: index,
            isHeading: false,
            rowId: incident.incident_id,
            data: {
              isActive: false,
              snap_case_number: incident.snap_case_number,
              claim_number: incident.claim_number || '-',
              employee_name: incident.employee_name || '-',
              employer_name: incident.company_name || '-',
              employer_phone: incident.employer_phone || '-',
              date_of_incident: incident.date_of_incident || '-',
              action: actionItems,
            },
          };
        });

        setAllData(incidents);
        setPagination(prev => ({
          ...prev,
          totalRecords: incidents.length,
          page: 1,
          sort_by: prev.sort_by || 'date_of_incident',
          sort_order: prev.sort_order || 'desc'
        }));
      }
    } catch (error) {
      console.error('Error fetching missing claim numbers:', error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error('Failed to load missing claim numbers');
      }
    } finally {
      setIsLoading(false);
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  const deleteIncidentHandler = async (incident) => {
    if (!incident?.incident_id) {
      if (Toast && typeof Toast.error === 'function') {
        Toast.error('Incident not found');
      }
      return;
    }

    if (Loader && typeof Loader === 'function') {
      Loader(true);
    }
    try {
      const response = await IncidentService.deleteIncident(incident.incident_id);
      if (response && (response.code === OK || response.code === 200)) {
        if (Toast && typeof Toast.success === 'function') {
          Toast.success('Incident deleted successfully');
        }
        if (typeof onIncidentDeleted === 'function') {
          onIncidentDeleted();
        }
        await fetchData();
      } else {
        if (Toast && typeof Toast.error === 'function') {
          Toast.error(response?.message || 'Failed to delete incident');
        }
      }
    } catch (error) {
      console.error('Error deleting incident:', error);
      if (Toast && typeof Toast.error === 'function') {
        const msg =
          error?.data?.message || error?.message || 'Failed to delete incident';
        Toast.error(msg);
      }
    } finally {
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  const viewReferralHandler = async (incident) => {
    if (!incident.visit_id || !incident.incident_id) {
      if (Toast && typeof Toast.error === 'function') {
        Toast.error(incident.visit_id ? "Incident ID not found" : "Visit ID not found for this incident");
      }
      return;
    }

    setShowReferralModal(true);
    setSelectedIncident(incident);
    setReferrals([]);
    setIsLoadingReferrals(true);

    try {
      const response = await EmrVisitsService.getReferralSelection(incident.visit_id);
      
      if (response?.code !== 200 || !response.data) {
        setReferrals([]);
        setIsLoadingReferrals(false);
        return;
      }

      // Extract referrals from response using similar logic as IncidentList
      const referralData = response.data;
      
      // Helper: Extract referral object from response
      const extractReferralObject = (data) => {
        if (Array.isArray(data)) return data[0];
        if (typeof data !== 'object') return null;
        const keys = Object.keys(data);
        return keys.length > 0 && !isNaN(keys[0]) ? data[keys[0]] : data;
      };

      // Helper: Format referral name from field key
      const formatReferralName = (key) => {
        return key.replace('_referral', '').replace(/_/g, ' ')
          .split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      };

      // Helper: Extract referral fields and provider IDs
      const extractReferrals = (referralObject) => {
        const referrals = [];
        Object.keys(referralObject).forEach(key => {
          if (key.endsWith('_referral') && referralObject[key]) {
            const providerId = referralObject[key];
            if (typeof providerId === 'string' && providerId.trim()) {
              referrals.push({
                referralName: formatReferralName(key),
                providerId: providerId
              });
            }
          }
        });
        return referrals;
      };

      // Helper: Fetch provider names
      const fetchProviderNames = async (referrals) => {
        try {
          const response = await ReferralProviderService.getReferralProviders(null, {
            perPage: 1000,
            page: 1
          });
          
          if (!response?.data || !Array.isArray(response.data)) return {};
          
          const providersMap = {};
          response.data.forEach(provider => {
            providersMap[provider.referral_provider_id] = provider;
          });
          
          return referrals.reduce((acc, referral) => {
            const provider = providersMap[referral.providerId];
            acc[referral.providerId] = provider 
              ? `${provider.first_name || ''} ${provider.last_name || ''}`.trim() 
              : 'N/A';
            return acc;
          }, {});
        } catch (error) {
          console.error("Error fetching provider details:", error);
          return {};
        }
      };

      const referralObject = extractReferralObject(referralData);
      if (!referralObject) {
        setReferrals([]);
        setIsLoadingReferrals(false);
        return;
      }

      const referrals = extractReferrals(referralObject);
      if (referrals.length > 0) {
        const providerNames = await fetchProviderNames(referrals);
        const parsedReferrals = referrals.map(ref => ({
          ...ref,
          providerName: providerNames[ref.providerId] || 'N/A'
        }));
        setReferrals(parsedReferrals);
      } else {
        setReferrals([]);
      }
      
      setIsLoadingReferrals(false);
    } catch (error) {
      console.error("Error fetching referrals:", error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Failed to fetch referrals for this incident");
      }
      setReferrals([]);
      setIsLoadingReferrals(false);
    }
  };

  const closeReferralModal = () => {
    setShowReferralModal(false);
    setSelectedIncident(null);
    setReferrals([]);
  };

  const openClaimNumberModal = (incident) => {
    setShowClaimNumberModal(true);
    setSelectedIncident(incident);
    setClaimNumber(incident.claim_number || '');
  };

  const closeClaimNumberModal = () => {
    setShowClaimNumberModal(false);
    setSelectedIncident(null);
    setClaimNumber('');
    setIsSavingClaimNumber(false);
  };

  const handleClaimNumberChange = (value) => {
    setClaimNumber(value);
  };

  const saveClaimNumber = async () => {
    if (!selectedIncident || !selectedIncident.incident_id) {
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Incident not found");
      }
      return;
    }

    setIsSavingClaimNumber(true);

    try {
      if (Loader && typeof Loader === 'function') {
        Loader(true);
      }
      
      const response = await IncidentService.updateClaimNumber(selectedIncident.incident_id, claimNumber);

      if (response && (response.code === 200 || response.code === OK)) {
        if (Toast && typeof Toast.success === 'function') {
          Toast.success("Claim number updated successfully");
        }
        closeClaimNumberModal();
        fetchData(); // Refresh the list
      } else {
        if (Toast && typeof Toast.error === 'function') {
          Toast.error(response?.message || "Failed to update claim number");
        }
      }
    } catch (error) {
      console.error("Error updating claim number:", error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Failed to update claim number");
      }
    } finally {
      setIsSavingClaimNumber(false);
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleClose = () => {
    if (originalPositionRef.current) {
      setPositionState(originalPositionRef.current);
      setTimeout(() => {
        setIsFullscreen(false);
        setPositionState(null);
        originalPositionRef.current = null;
        hasExpandedRef.current = false;
        onClose();
      }, 300);
    } else {
      setIsFullscreen(false);
      setPositionState(null);
      hasExpandedRef.current = false;
      onClose();
    }
  };

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const tableHeaders = [
    {
      label: "Snap Case Number",
      key: "snap_case_number",
      type: "",
      inputType: "TextInput",
      sortName: "snap_case_number",
    },
    {
      label: "Employer Name",
      key: "employer_name",
      type: "",
      inputType: "TextInput",
      sortName: "employer_name",
    },
    {
      label: "Employer Phone",
      key: "employer_phone",
      type: "",
      inputType: "TextInput",
      sortName: "employer_phone",
    },
    {
      label: "Patient Name",
      key: "employee_name",
      type: "",
      inputType: "TextInput",
      sortName: "employee_name",
    },
    {
      label: "Injury Date",
      key: "date_of_incident",
      type: "",
      inputType: "TextInput",
      sortName: "date_of_incident",
    },
    {
      label: "Claim Number",
      key: "claim_number",
      type: "",
      inputType: "TextInput",
      sortName: "claim_number",
    },
    { label: "Action", key: "action", type: "Actions" },
  ];

  if (!isOpen) return null;

  const effectivePosition = (isOpen && initialPosition && positionState === null && !hasExpandedRef.current)
    ? initialPosition
    : (positionState != null ? positionState : null);

  return (
    <div
      className="tblviewcard flip fullscreen-card"
      style={{
        position: 'fixed',
        top: effectivePosition ? `${effectivePosition.top}px` : '0',
        left: effectivePosition ? `${effectivePosition.left}px` : '0',
        width: effectivePosition ? `${effectivePosition.width}px` : '100vw',
        height: effectivePosition ? `${effectivePosition.height}px` : '100vh',
        zIndex: 1040,
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: effectivePosition ? '8px' : '0',
        boxShadow: effectivePosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <div className="card mb-0 border-0 shadow-none" style={{ height: '100%', margin: 0 }}>
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="text-md font-weight-bold mb-0">Missing Claim Numbers</h6>
            <div>
              <a
                className="btn sh_btniconnew"
                style={{ cursor: 'pointer' }}
                onClick={handleClose}
                title="Close"
              >
                <CloseIcon />
              </a>
            </div>
          </div>
        </div>
        <div className="card-body px-0 pt-0 mt-2 pb-2" style={{ height: 'calc(100% - 60px)', overflow: 'auto' }}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <TableGrid
                tableHeaders={tableHeaders}
                tableRows={{
                  type: "default",
                  status: 200,
                  data: tableData,
                }}
                pagination={pagination}
                fetchTableRecords={handlePaginationChange}
                isSort={true}
              />
              {isFullscreen && allData.length > 0 && pagination.totalRecords > pagination.perPage && (
                <div className="px-3 pb-2">
                  <Pagination
                    pagination={pagination}
                    fetchTableRecords={handlePaginationChange}
                    tableRows={{ data: tableData }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Claim Number Modal */}
      {showClaimNumberModal && (
        <Modal show={showClaimNumberModal} onHide={closeClaimNumberModal}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Claim Number</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Claim Number</label>
              <FormInputs
                type="TextInput"
                value={claimNumber}
                changeHandler={handleClaimNumberChange}
                className="form-control"
                placeholder="Enter claim number"
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Buttons
              className="btn btn-secondary"
              clickHandler={closeClaimNumberModal}
              label="Cancel"
              disabled={isSavingClaimNumber}
            />
            <Buttons
              className="btn btn-primary"
              clickHandler={saveClaimNumber}
              label={isSavingClaimNumber ? "Saving..." : "Save"}
              disabled={isSavingClaimNumber}
            />
          </Modal.Footer>
        </Modal>
      )}

      {/* Referral Modal */}
      {showReferralModal && (
        <Modal show={showReferralModal} onHide={closeReferralModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Referrals for Incident: {selectedIncident?.snap_case_number || selectedIncident?.claim_number || 'N/A'}
              {selectedIncident?.claim_number && selectedIncident?.snap_case_number && (
                <span className="text-muted"> ({selectedIncident.claim_number})</span>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isLoadingReferrals ? (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : referrals.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Referral Type</th>
                      <th>Provider Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((referral, index) => (
                      <tr key={index}>
                        <td>{referral.referralName || 'N/A'}</td>
                        <td>{referral.providerName || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center p-4">
                <p>No referrals found for this incident.</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Buttons
              className="btn btn-secondary"
              clickHandler={closeReferralModal}
              label="Close"
            />
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default MissingClaimNumbersFullScreen;
